import { AppRole } from "@revelate/types";

export type RevenueIntelligenceChatDataScope = "company" | "team" | "self";

export type RevenueIntelligenceChatAccessContext = {
  userId: string;
  companyId: string;
  role: AppRole;
  teamIds: string[];
};

export type RevenueIntelligenceChatAccessPolicy = {
  allowedRoles: AppRole[];
  companyId: string;
  dataScope: RevenueIntelligenceChatDataScope;
  enforcement: "company_role_gate" | "server_filtered";
  subjectTeamIds?: string[];
  subjectUserIds?: string[];
};

export type RevenueIntelligenceChatAccessDecision = {
  allowed: boolean;
  reason:
    | "allowed"
    | "company_mismatch"
    | "missing_actor"
    | "role_not_allowed"
    | "scope_not_server_filtered"
    | "self_scope_mismatch"
    | "team_scope_mismatch";
};

export function evaluateRevenueIntelligenceChatAccess(
  context: RevenueIntelligenceChatAccessContext | null,
  policy: RevenueIntelligenceChatAccessPolicy,
): RevenueIntelligenceChatAccessDecision {
  if (!context) return { allowed: false, reason: "missing_actor" };
  if (context.companyId !== policy.companyId) {
    return { allowed: false, reason: "company_mismatch" };
  }
  if (!policy.allowedRoles.includes(context.role)) {
    return { allowed: false, reason: "role_not_allowed" };
  }

  if (policy.dataScope === "company") {
    return { allowed: true, reason: "allowed" };
  }

  if (policy.enforcement !== "server_filtered") {
    return { allowed: false, reason: "scope_not_server_filtered" };
  }

  if (policy.dataScope === "self") {
    const subjectUserIds = policy.subjectUserIds ?? [];
    const onlyCurrentUser = subjectUserIds.length > 0
      && subjectUserIds.every(userId => userId === context.userId);
    return onlyCurrentUser
      ? { allowed: true, reason: "allowed" }
      : { allowed: false, reason: "self_scope_mismatch" };
  }

  const subjectTeamIds = policy.subjectTeamIds ?? [];
  const actorTeamIds = new Set(context.teamIds);
  const onlyActorTeams = subjectTeamIds.length > 0
    && subjectTeamIds.every(teamId => actorTeamIds.has(teamId));
  return onlyActorTeams
    ? { allowed: true, reason: "allowed" }
    : { allowed: false, reason: "team_scope_mismatch" };
}
